import React, { useEffect, useMemo, useRef, useState } from "react";

import Hero from "./Hero";
import Projects from "./Projects";
import Footer from "./Footer";

import jimAndPam from "../assets/jimandpam.png";
import ToolsGrid from "./tools/ToolsGrid";
import { detectRegion } from "../services/locationApi";
import {
  getConfirmedLocationContext,
  persistDetectedLocation,
} from "../utils/locationStorage";
import { useBrand } from "../branding/BrandContext";
import BrandText, { BrandMark } from "../branding/BrandText";

// 🔑 SINGLE SOURCE OF TRUTH
import { getProjectsForBrand } from "../config/projects";

// 👉 Lightweight FAQ Subsection (accordion)
function FAQPreview() {
  const [open, setOpen] = useState(null);

  const faqs = [
    {
      q: "What is HomeHelper and how does it help me?",
      a: "HomeHelper gives you clarity before you spend money. We analyze your project, show realistic cost expectations, and connect you with licensed contractors who are actively available—so you avoid guesswork, overpriced quotes, and wasted time."
    },
    {
      q: "How quickly will contractors contact me?",
      a: "Most homeowners hear back within minutes to a few hours. We prioritize active contractors who are ready to engage immediately—not days later."
    },
    {
      q: "Are the contractors licensed and insured?",
      a: "We prioritize contractors who are licensed, bonded, and insured according to local requirements. We also continuously monitor performance and responsiveness to maintain quality."
    },
    {
      q: "Do I have to hire someone after submitting?",
      a: "No. There is zero obligation. You can use HomeHelper to understand your project and pricing—even if you're just planning."
    },
    {
      q: "How accurate are the cost estimates?",
      a: "Our estimates are data-driven and designed to give you a realistic range based on your project and location. Final pricing depends on contractor inspection, but our goal is to eliminate surprises."
    },
    {
      q: "Will I get multiple quotes?",
      a: "In many cases, yes. Depending on availability, we connect you with multiple qualified contractors so you can compare options and choose confidently."
    }
  ];

  return (
    <section style={styles.section}><br />
      <div style={styles.inner}>
        <h2 style={styles.title}>Common Questions</h2>
        <p style={styles.subtitle}>
          Quick answers to help you feel confident before getting started.
        </p>

        <div style={styles.list}>
          {faqs.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} style={styles.card}>
                <div
                  style={styles.row}
                  onClick={() => setOpen(isOpen ? null : i)}
                >
                  <span style={styles.q}><BrandText>{item.q}</BrandText></span>
                  <span style={styles.icon}>{isOpen ? "−" : "+"}</span>
                </div>
                {isOpen && <p style={styles.a}><BrandText>{item.a}</BrandText></p>}
              </div>
            );
          })}
        </div>

        <div style={styles.cta}>
          <h3 style={{ marginBottom: 6 }}>Ready to start your project?</h3>
          <p style={{ marginBottom: 10 }}>
            Get pricing clarity and connect with trusted pros in minutes.
          </p>
          <button
            style={styles.button}
            onClick={() => (window.location.href = "/")}
          >
            Start Your Project
          </button>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const brand = useBrand();
  const visibleProjects = useMemo(
    () => getProjectsForBrand(brand?.brandKey),
    [brand?.brandKey]
  );
  const projectOptions = useMemo(
    () => visibleProjects.map(({ slug, label }) => ({ slug, label })),
    [visibleProjects]
  );

  const [project, setProject] = useState("");

  const initFromUrlDoneRef = useRef(false);

  const debugLog = (...args) => {
    if (process.env.NODE_ENV !== "production") {
      console.log(...args);
    }
  };

  const getZipFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const queryZip = params.get("zip") || params.get("zipcode");

    if (queryZip && /^\d{5}(-\d{4})?$/.test(queryZip.trim())) {
      return queryZip.trim();
    }

    const pathParts = window.location.pathname.split("/").filter(Boolean);
    const pathZip = pathParts.find((part) => /^\d{5}(-\d{4})?$/.test(part));

    return pathZip || "";
  };

  const applyZipToInputs = (zipValue) => {
    if (!zipValue) return;

    const selectors = [
      'input[name="zip"]',
      'input[name="zipcode"]',
      'input[id="zip"]',
      'input[id="zipcode"]',
      'input[placeholder*="ZIP" i]',
      'input[placeholder*="zip" i]'
    ];

    const input = document.querySelector(selectors.join(", "));
    if (!input) {
      debugLog("[HomePage] ZIP input not found");
      return;
    }

    const nativeSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      "value"
    )?.set;

    nativeSetter?.call(input, zipValue);
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  };

  useEffect(() => {
    if (initFromUrlDoneRef.current) return;
    initFromUrlDoneRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const cid = params.get("cid");
    const zipFromUrl = getZipFromUrl();

    if (cid) {
      localStorage.setItem(
        "homehelper_campaign_id",
        JSON.stringify({
          value: cid,
          expiresAt: Date.now() + 24 * 60 * 60 * 1000
        })
      );
      debugLog("[HomePage] Stored cid:", cid);
    }

    if (zipFromUrl) {
      debugLog("[HomePage] Setting ZIP from URL:", zipFromUrl);
      setTimeout(() => applyZipToInputs(zipFromUrl), 0);
    }
  }, []);

  useEffect(() => {
    const confirmedLocation = getConfirmedLocationContext();
    if (confirmedLocation.regionSlug || confirmedLocation.zip) {
      return undefined;
    }

    const controller = new AbortController();

    const hydrateDetectedLocation = async () => {
      try {
        const payload = await detectRegion({ signal: controller.signal });

        if (
          !payload?.region ||
          payload?.fallback?.askForZip ||
          !["high", "medium"].includes(String(payload?.confidence || "").toLowerCase())
        ) {
          return;
        }

        persistDetectedLocation({
          regionId: payload.region.id || "",
          regionSlug: payload.region.slug || "",
          regionName: payload.region.name || "",
          zip: payload.detected?.zip || "",
          source: payload.source || "detected",
          confidence: payload.confidence || "",
          requiresConfirmation: true,
        });
      } catch (error) {
        if (!controller.signal.aborted) {
          debugLog("[HomePage] Silent region detection skipped", error?.message || error);
        }
      }
    };

    hydrateDetectedLocation();

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <main>
      <Hero
        personaSrc={jimAndPam}
        project={project}
        setProject={setProject}
        projectOptions={projectOptions}
      />

      <div className="container">
        <Projects projects={visibleProjects} />

        {/* 👉 FAQ Subsection (placed ABOVE Helpful Planning Tools) */}
        <FAQPreview />

        {/* Helpful Planning Tools */}
        <div>
          <ToolsGrid />
        </div>
      </div>

      {/* 🔒 Legal Disclaimer */}
      <div
        style={{
          margin: "20px",
          fontSize: 14,
          color: "#6b7280",
          lineHeight: 1.6,
          textAlign: "center",
          justifyItems: "center",
        }}
      >
        <strong>Disclaimer</strong>
        <p
          style={{
            maxWidth: 800,
            fontSize: 11,
            color: "#6b7280",
            lineHeight: 1.6,
            textAlign: "center",
            paddingBottom: 30,
          }}
        >
          <BrandMark domain /> provides informational tools, estimates, and guidance to help
          homeowners understand home improvement projects. Information presented on
          this website is for general informational purposes only and does not
          constitute professional, legal, financial, or construction advice.{" "}
          <BrandMark domain /> does not perform construction services and does not guarantee
          pricing, timelines, code compliance, or contractor performance. All project
          decisions, inspections, permits, and work should be reviewed and completed
          by properly licensed professionals in accordance with local regulations.
          Use of this website constitutes acceptance of these terms.
        </p>
      </div>

      <Footer />
    </main>
  );
}

const styles = {
  section: {
    marginBottom: 10,
    width: "91%",
    justifySelf: "center",
  },
  inner: {
    borderRadius: 16,
    padding: 10,
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    maxWidth: 700,
    margin: "0 auto",
    
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 6,
  },
  subtitle: {
    color: "#6b7280",
    marginBottom: 16,
  },
  list: {
    display: "grid",
    gap: 10,
  },
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 14,
    background: "#fff",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    cursor: "pointer",
  },
  q: {
    fontWeight: 600,
  },
  a: {
    marginTop: 8,
    color: "#444",
    lineHeight: 1.6,
  },
  icon: {
    fontSize: 20,
  },
  cta: {
    marginTop: 16,
    textAlign: "center",
    background: "#f3f4f6",
    borderRadius: 12,
    padding: 16,
  },
  button: {
    padding: "10px 18px",
    borderRadius: 8,
    border: "none",
    background: "#4f46e5",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 600,
  },
};
